import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { InvoicePreview } from './components/InvoicePreview';
import { InvoiceForm } from './components/InvoiceForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { useInvoice } from '../../core/hooks/useInvoice';
import { useClients } from '../../core/hooks/useClients';
import { useAuth } from '../../core/contexts/AuthContext';
import { DollarSign, ArrowLeft, Edit } from 'lucide-react';

export const InvoiceDetailPage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const { invoice, loading, error, updateInvoice } = useInvoice(invoiceId || null);
  const { clients } = useClients(organizationId);
  const [editMode, setEditMode] = useState(false);

  const client = invoice ? clients.find((c) => c.id === invoice.clientId) : null;

  const handleUpdate = async (data: any) => {
    if (!invoiceId) return;
    try {
      await updateInvoice(data);
      setEditMode(false);
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = () => {
    // TODO: Implement email functionality via Firebase Function
    alert('Email functionality will be implemented via Firebase Function');
  };

  const handleDownload = () => {
    // TODO: Implement PDF download
    alert('PDF download will be implemented');
  };

  if (loading) {
    return <LoadingState message="Loading invoice..." />;
  }

  if (error || !invoice) {
    return <ErrorState message={error?.message || 'Invoice not found'} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Invoice ${invoice.number}`}
        description="View and manage invoice details"
        icon={DollarSign}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/invoices')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button variant="outline" onClick={() => setEditMode(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </div>
        }
      />

      {editMode ? (
        <Dialog open={editMode} onOpenChange={setEditMode}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Invoice</DialogTitle>
            </DialogHeader>
            <InvoiceForm
              invoice={invoice}
              onSubmit={handleUpdate}
              onCancel={() => setEditMode(false)}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <InvoicePreview
          invoice={invoice}
          clientName={client?.name}
          onPrint={handlePrint}
          onEmail={handleEmail}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
};
